/**
 * MyDocumentsPage — Documentos del colaborador.
 *
 * Usada en AMBOS contextos:
 *   - Caso A: /colaboradores/documents (PortalLayout, portalApiClient)
 *   - Caso B: /portal/documents (AppLayout, apiClient ERP)
 */
import { DataTable } from '@/components/ds/DataTable'
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { FileText, Download, Eye, Loader2, ChevronDown, ChevronRight, Folder } from 'lucide-react'
import { bajarArchivo, documentoGuardado, useVisorPdf } from '@/components/VisorPdf'
import apiClient from '@/lib/apiClient'
import { portalApiClient } from '@/store/portalAuthStore'

/**
 * «Ver» abre el PDF en el visor, desde donde se descarga. Lo que no es PDF (una
 * planilla, un Word) no se puede mostrar en el navegador: se sigue descargando.
 */
function AccionDelDocumento({ docId, docName, mimeType }: { docId: string; docName: string; mimeType?: string }) {
  const { pathname } = useLocation()
  const client = pathname.startsWith('/colaboradores') ? portalApiClient : apiClient
  const { ver, visor } = useVisorPdf()
  const [loading, setLoading] = useState(false)
  const ruta = `/employee/documents/${docId}/download/`
  const esPdf = (mimeType || '').toLowerCase() === 'application/pdf'

  if (esPdf) {
    return (
      <>
        <button
          onClick={() => ver({
            titulo: docName,
            nombreArchivo: docName.toLowerCase().endsWith('.pdf') ? docName : `${docName}.pdf`,
            ...documentoGuardado(client, ruta),
          })}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          aria-label={`Ver ${docName}`}
        >
          <Eye size={13} aria-hidden="true" />
          Ver
        </button>
        {visor}
      </>
    )
  }

  const descargar = async () => {
    setLoading(true)
    try {
      const contenido = await documentoGuardado(client, ruta).urlDeDescarga!()
      bajarArchivo(contenido, docName)
    } finally {
      setLoading(false)
    }
  }
  return (
    <button
      onClick={descargar}
      disabled={loading}
      className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50"
      aria-label={`Descargar ${docName}`}
    >
      {loading ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Download size={13} aria-hidden="true" />}
      Descargar
    </button>
  )
}
import { usePortalDocuments } from '../hooks/useEmployeePortal'
import { EmptyState } from '@/components/ds/EmptyState'
import { Skeleton } from '@/components/ds/Skeleton'
import { GlassPanel } from '@/components/ds/GlassPanel'
import { SearchInput } from '@/components/ds/SearchInput'
import { PaginaPortal } from '../components/PaginaPortal'

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

const SIN_CARPETA = 'Otros documentos'

type PortalDoc = {
  id: string
  name: string
  description?: string
  document_type_name?: string
  folder_name?: string
  folder_order?: number
  file_size: number
  mime_type?: string
}

type Grupo = { name: string; order: number; docs: PortalDoc[] }

/**
 * Agrupa los documentos por carpeta respetando el orden de la carpeta digital
 * de RRHH (`folder_order`, que viene de `FolderTemplate.order`). Antes la página
 * los listaba planos con una columna "Carpeta", así que un colaborador con
 * varios meses de liquidaciones tenía que leer la tabla entera para encontrar
 * su contrato.
 */
function agruparPorCarpeta(docs: PortalDoc[]): Grupo[] {
  const mapa = new Map<string, Grupo>()
  for (const doc of docs) {
    const name = doc.folder_name?.trim() || SIN_CARPETA
    const existente = mapa.get(name)
    if (existente) {
      existente.docs.push(doc)
      // La carpeta hereda el orden más bajo de sus documentos (los que no
      // tienen plantilla llegan con 9999 y no deben arrastrar al grupo).
      existente.order = Math.min(existente.order, doc.folder_order ?? 9999)
    } else {
      mapa.set(name, { name, order: doc.folder_order ?? 9999, docs: [doc] })
    }
  }
  return [...mapa.values()].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name, 'es'))
}

export default function MyDocumentsPage() {
  const [search, setSearch] = useState('')
  const [cerradas, setCerradas] = useState<Record<string, boolean>>({})
  const { data, isLoading, error } = usePortalDocuments(search ? { search } : undefined)
  const grupos = useMemo(() => agruparPorCarpeta(data?.results ?? []), [data?.results])
  const documents: PortalDoc[] = data?.results ?? []

  return (
    <PaginaPortal
      titulo="Carpeta digital"
      bajada="Documentos laborales compartidos por RRHH, agrupados por carpeta."
      acciones={
        <SearchInput
          label="Buscar documentos"
          placeholder="Buscar documentos…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-72 max-w-full"
        />
      }
    >
      {isLoading ? (
        <GlassPanel><Skeleton variant="table" lines={5} /></GlassPanel>
      ) : error ? (
        <EmptyState variant="error" title="No se pudieron cargar los documentos" />
      ) : documents.length === 0 ? (
        search
          ? <EmptyState variant="filtered" title="Sin resultados" description={`Ningún documento coincide con «${search}».`} />
          : <EmptyState icon={<FileText size={28} aria-hidden="true" />} title="Sin documentos disponibles" description="RRHH todavía no comparte documentos en la carpeta." />
      ) : (
        <div className="flex flex-col gap-4">
          {grupos.map((grupo) => {
            // Al buscar se expande todo: colapsar resultados esconde justo lo
            // que el colaborador está tratando de encontrar.
            const abierta = search ? true : !cerradas[grupo.name]
            const panelId = `carpeta-${grupo.name.replace(/\s+/g, '-')}`
            return (
              <GlassPanel key={grupo.name} as="section" padded={false} className="overflow-hidden">
                <h2>
                  <button
                    type="button"
                    onClick={() => setCerradas((prev) => ({ ...prev, [grupo.name]: abierta }))}
                    aria-expanded={abierta}
                    aria-controls={panelId}
                    disabled={!!search}
                    className={`w-full flex items-center gap-2 px-5 py-3.5 text-left hover:bg-hover ${abierta ? 'border-b border-divider' : ''}
                               disabled:hover:bg-transparent
                               focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring`}
                  >
                    {abierta
                      ? <ChevronDown size={15} className="text-content-subtle shrink-0" aria-hidden="true" />
                      : <ChevronRight size={15} className="text-content-subtle shrink-0" aria-hidden="true" />}
                    <Folder size={16} className="text-primary shrink-0" aria-hidden="true" />
                    <span className="font-semibold text-content">{grupo.name}</span>
                    <span className="text-xs text-content-muted">
                      {grupo.docs.length} {grupo.docs.length === 1 ? 'documento' : 'documentos'}
                    </span>
                  </button>
                </h2>

                {abierta && (
                  <div id={panelId}>
                    {/* Sin superficie propia: el panel ya es la carpeta. */}
                    <DataTable
                      surface={false}
                      columns={[
                        {
                          key: 'nombre', header: 'Nombre', sortable: true,
                          sortValue: doc => doc.name,
                          render: doc => (
                            <div className="flex items-center gap-2 font-medium text-content">
                              <FileText size={14} className="text-content-subtle shrink-0" aria-hidden="true" />
                              {doc.name}
                            </div>
                          ),
                        },
                        { key: 'tipo', header: 'Tipo', sortable: true, sortValue: doc => doc.document_type_name || '', render: doc => doc.document_type_name || '—' },
                        {
                          key: 'tamano', header: 'Tamaño', numeric: true, sortable: true,
                          // `formatSize` imprime «1,2 MB»: como texto, 900 KB iría después.
                          sortValue: doc => doc.file_size,
                          render: doc => <span className="whitespace-nowrap">{formatSize(doc.file_size)}</span>,
                        },
                        {
                          key: 'descarga', header: <span className="sr-only">Ver o descargar</span>, width: 96,
                          render: doc => <AccionDelDocumento docId={doc.id} docName={doc.name} mimeType={doc.mime_type} />,
                        },
                      ]}
                      rows={grupo.docs}
                      minWidth={480}
                      caption={`Documentos de ${grupo.name}`}
                    />
                  </div>
                )}
              </GlassPanel>
            )
          })}
        </div>
      )}
    </PaginaPortal>
  )
}
